/**
 * Servicio de logging que controla la salida de logs según el entorno
 * Solo muestra logs en entornos de desarrollo
 */

/**
 * Tipo para los niveles de log disponibles
 */
export type NivelLog = 'info' | 'warn' | 'error' | 'debug';

/**
 * Interfaz para el servicio de logging
 */
export interface ServicioLogger {
  info: (mensaje: string, ...datos: readonly unknown[]) => void;
  warn: (mensaje: string, ...datos: readonly unknown[]) => void;
  error: (mensaje: string, ...datos: readonly unknown[]) => void;
  debug: (mensaje: string, ...datos: readonly unknown[]) => void;
}

/**
 * Determina si estamos en entorno de producción
 * Busca import.meta.env.PROD o VITE_ENTORNO=produccion
 */
const esProduccion = (): boolean => {
  return (
    import.meta.env.PROD || 
    import.meta.env.VITE_ENTORNO === 'produccion'
  );
};

/**
 * Implementación del logger que no hace nada
 * Utilizado en producción para evitar logs
 */
const loggerSilencioso: ServicioLogger = {
  info: () => {},
  warn: () => {},
  error: () => {},
  debug: () => {},
};

/**
 * Implementación del logger que usa console
 * Utilizado en desarrollo
 */
const loggerDesarrollo: ServicioLogger = {
  info: (mensaje: string, ...datos: readonly unknown[]): void => {
    console.info(`[INFO] ${mensaje}`, ...datos);
  },
  warn: (mensaje: string, ...datos: readonly unknown[]): void => {
    console.warn(`[WARN] ${mensaje}`, ...datos);
  },
  error: (mensaje: string, ...datos: readonly unknown[]): void => {
    console.error(`[ERROR] ${mensaje}`, ...datos);
  },
  debug: (mensaje: string, ...datos: readonly unknown[]): void => {
    console.debug(`[DEBUG] ${mensaje}`, ...datos);
  },
};

/**
 * Logger que se comporta según el entorno
 */
const logger: ServicioLogger = esProduccion() ? loggerSilencioso : loggerDesarrollo;

export default logger;
